import React, { useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { useStore } from '@store/StoreProvider'
import { t } from '@store/I18nStore'
import {
  getLandingCompare,
  LANDING_PRICING,
  formatMoneyDual,
  type CompareCell,
} from '../pricingConfig'

const CheckIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={`cmp-ok ${className ?? ''}`} viewBox="0 0 24 24" aria-label={t('app.words.included')}>
    <path d="M4.5 12.5l5 5 10-11" />
  </svg>
)

function CellValue({ v }: { v: CompareCell }) {
  const unlimited = t('billing.limits.no_limits')
  if (v === true) return <CheckIcon />
  if (v === false) return <span className="cmp-no" aria-label={t('app.words.not_included')} />
  if (typeof v === 'string' && v === unlimited) {
    return <span className="cmp-inf">{v}</span>
  }
  return <>{v}</>
}

export const CompareSection: React.FC = observer(() => {
  const [mobCol, setMobCol] = useState(1)
  const { i18n } = useStore()
  void i18n.language
  void i18n.version
  const bodyRef = React.useRef<HTMLTableSectionElement>(null)
  const rows = getLandingCompare()
  const perOp = t('billing.price.per_operator_short')
  const unlimited = t('billing.limits.no_limits')

  const tabs = [
    { col: 1, name: t('billing.plans.name_start'), price: `${formatMoneyDual(LANDING_PRICING.plans.start)}${perOp}` },
    { col: 2, name: t('billing.plans.name_pro'), price: `${formatMoneyDual(LANDING_PRICING.plans.pro)}${perOp}` },
    { col: 3, name: t('billing.plans.name_max'), price: `${formatMoneyDual(LANDING_PRICING.plans.max)}${perOp}` },
  ]

  useEffect(() => {
    const body = bodyRef.current
    if (!body) return
    const rowEls = body.querySelectorAll('.cmp-row')
    if (!('IntersectionObserver' in window)) {
      rowEls.forEach((r) => r.classList.add('cmp-row-in'))
      return
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((en) => {
          if (!en.isIntersecting) return
          const el = en.target as HTMLElement
          const idx = parseInt(el.getAttribute('data-i') || '0', 10) || 0
          setTimeout(() => el.classList.add('cmp-row-in'), Math.min(idx, 14) * 55)
          io.unobserve(el)
        })
      },
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' },
    )
    rowEls.forEach((r, j) => {
      r.setAttribute('data-i', String(j))
      io.observe(r)
    })
    return () => io.disconnect()
  }, [i18n.language, i18n.version])

  return (
    <section id="compare" className="relative py-14 sm:py-20 overflow-hidden">
      <div className="hidden xl:block absolute inset-0 pointer-events-none select-none" aria-hidden="true">
        <span className="deco d1" />
        <span className="deco d2" />
        <span className="deco d3" />
        <span className="deco d4" />
        <span className="dotline dl1" />
        <span className="dotline dl2" />
      </div>
      <div className="relative max-w-6xl mx-auto px-5 sm:px-8">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <div className="reveal inline-flex items-center gap-2 text-xs font-semibold text-brand2 uppercase tracking-widest mb-4">
            <span className="w-8 h-px bg-brand2" /> {t('landing.compare.badge')}{' '}
            <span className="w-8 h-px bg-brand2" />
          </div>
          <h2 className="reveal text-3xl sm:text-5xl font-extrabold">
            {t('landing.compare.title')}{' '}
            <span className="text-gradient">{t('landing.compare.title_accent')}</span>
          </h2>
          <p className="reveal mt-3 text-slate-400">{t('landing.compare.operators_note')}</p>
        </div>

        <div className="reveal cmp-mob">
          <div className="cmp-tabs">
            {tabs.map((tab) => (
              <button
                key={tab.col}
                type="button"
                data-col={tab.col}
                className={mobCol === tab.col ? 'on' : undefined}
                onClick={() => setMobCol(tab.col)}
              >
                <b>{tab.name}</b>
                <span>{tab.price}</span>
              </button>
            ))}
          </div>
          <div className="cmp-mob-body">
            {rows.map((row, i) => {
              if ('g' in row) {
                return (
                  <div key={`g-${row.g}`} className="cmp-mob-g">
                    {row.g}
                  </div>
                )
              }
              const v = row[mobCol as 1 | 2 | 3]
              const isBool = v === true || v === false
              const isInf = typeof v === 'string' && v === unlimited
              return (
                <div key={`m-${i}-${row[0]}`} className="cmp-mob-r">
                  <span className="f">{row[0]}</span>
                  <span className={`v${isBool ? ' ic' : ''}${isInf ? ' inf' : ''}`}>
                    {v === true ? (
                      <CheckIcon />
                    ) : v === false ? (
                      <span className="cmp-no" aria-label={t('app.words.not_included')} />
                    ) : isInf ? (
                      <span className="inf">{v}</span>
                    ) : (
                      v
                    )}
                  </span>
                </div>
              )
            })}
          </div>
        </div>

        <div className="reveal cmp-shell">
          <div className="cmp-scroll">
            <table className="cmp">
              <thead>
                <tr>
                  <th className="cmp-feat">{t('landing.compare.feature_column')}</th>
                  <th>
                    <span className="cmp-plan">{t('billing.plans.name_start')}</span>
                    <span className="cmp-price">
                      {formatMoneyDual(LANDING_PRICING.plans.start)}
                      <i>{perOp}</i>
                    </span>
                  </th>
                  <th className="cmp-hl">
                    <span className="cmp-badge">{t('billing.plans.popular')}</span>
                    <span className="cmp-plan">{t('billing.plans.name_pro')}</span>
                    <span className="cmp-price">
                      {formatMoneyDual(LANDING_PRICING.plans.pro)}
                      <i>{perOp}</i>
                    </span>
                  </th>
                  <th>
                    <span className="cmp-plan">{t('billing.plans.name_max')}</span>
                    <span className="cmp-price">
                      {formatMoneyDual(LANDING_PRICING.plans.max)}
                      <i>{perOp}</i>
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody ref={bodyRef}>
                {rows.map((row, i) => {
                  if ('g' in row) {
                    return (
                      <tr key={`g-${row.g}`} className="cmp-group cmp-row">
                        <td colSpan={4}>{row.g}</td>
                      </tr>
                    )
                  }
                  return (
                    <tr key={`r-${i}-${row[0]}`} className="cmp-row">
                      <td className="cmp-feat">{row[0]}</td>
                      <td>
                        <CellValue v={row[1]} />
                      </td>
                      <td className="cmp-hl">
                        <CellValue v={row[2]} />
                      </td>
                      <td>
                        <CellValue v={row[3]} />
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </section>
  )
})
